<script setup lang="ts">
import type {DiagramBlock} from '~/types/legacy'
import type {Populated} from '~/types/populated'
import ShadowBorder from '../managed-projects/shadow-border.vue'

const props = defineProps<{
  data: Populated<DiagramBlock>
}>()

const sections = computed(() => [
  {
    title: 'Hardware',
    items: props.data.hardware || [],
    gridCols: 'space-y-4',
  },
  {
    title: 'User Portal',
    items: props.data.userPortal || [],
    gridCols: 'grid grid-cols-1 md:grid-cols-3 gap-4',
  },
  {
    title: '3rd Parties',
    items: props.data.thirdParties || [],
    gridCols: 'space-y-4',
  },
])

const getMinHeight = (index: number) => {
  return index === 1 ? 'min-h-[180px]' : index === 0 ? 'min-h-[115px]' : 'min-h-[62px]'
}

const bottomBoxes = computed(() => [props.data.diagramSubtitleFirst, props.data.diagramSubtitleSecond])
</script>

<template>
  <div class="border-t">
    <Container>
      <div class="mx-auto max-w-7xl px-8 py-24 lg:px-0">
        <div class="mb-16 text-center">
          <Heading as="h2" size="caption" class="mb-4">{{ data.eyebrow }}</Heading>
          <Heading as="h3" size="medium">{{ data.title }}</Heading>
          <Paragraph class="mx-auto my-4 max-w-2xl text-gray-600">
            {{ data.body }}
          </Paragraph>

          <ShadowBorder position="top" />
        </div>

        <div class="mb-1 mt-32 flex justify-center">
          <div class="rounded-lg border-2 border-dashed border-main-600 bg-white px-16 py-6 shadow-sm">
            <Paragraph as="p" class="font-semibold text-center text-gray-700">
              {{ $t('warehouse.diagram.main_title') }}
            </Paragraph>
          </div>
        </div>

        <div class="mb-1 flex justify-center">
          <Icon name="material-symbols:arrow-cool-down" class="text-3xl text-main-500" />
        </div>

        <div class="mb-1 grid grid-cols-1 items-center gap-1 lg:grid-cols-[280px_auto_1fr_auto_280px]">
          <template v-for="(section, sectionIndex) in sections" :key="sectionIndex">
            <div class="rounded-lg border-2 border-dashed border-main-600 bg-white p-6 shadow-sm">
              <Heading class="mb-6 inline-block rounded-lg bg-main-300 px-3 py-1 text-sm font-normal">
                {{ section.title }}
              </Heading>
              <div :class="section.gridCols">
                <div
                  v-for="(item, index) in section.items"
                  :key="index"
                  :class="[
                    'rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-700 transition-colors hover:bg-gray-100',
                    getMinHeight(sectionIndex),
                    'flex items-center justify-center text-center',
                  ]"
                >
                  <span :class="sectionIndex === 0 ? 'flex items-center justify-center text-center' : ''">
                    {{ item.label }}
                  </span>
                </div>
              </div>
            </div>
            <div v-if="sectionIndex < sections.length - 1" class="my-1 flex justify-center">
              <div class="hidden md:block">
                <Icon
                  name="material-symbols:arrow-cool-down"
                  :class="['text-3xl text-main-500', sectionIndex % 2 === 0 ? '-rotate-[90deg]' : 'rotate-[90deg]']"
                />
              </div>
              <div class="md:hidden">
                <Icon name="material-symbols:arrow-cool-down" class="text-3xl text-main-500" />
              </div>
            </div>
          </template>
        </div>

        <div class="mb-1 flex justify-center gap-[13rem]">
          <Icon
            v-for="i in 2"
            :key="i"
            name="material-symbols:arrow-cool-down"
            class="rotate-[180deg] text-3xl text-main-500"
          />
        </div>

        <div class="flex justify-center gap-8">
          <div
            v-for="(text, index) in bottomBoxes"
            :key="index"
            class="w-52 rounded-lg border-2 border-dashed border-main-600 bg-white px-12 py-6 shadow-sm transition-shadow hover:shadow-md"
          >
            <p class="font-semibold text-center text-gray-700">
              {{ text }}
            </p>
          </div>
        </div>
      </div>
    </Container>
  </div>
</template>
